import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ActiveBadge } from '@/components/Badges';
import { EmptyState, LoadingState } from '@/components/States';
import Pagination from '@/components/Pagination';
import { Plus, Search, Edit2, Trash2, Power, Users, Loader2, Key } from 'lucide-react';
import { toast } from 'sonner';
import { ROLE_LABEL, USER_STATUS_LABEL } from '@/lib/labels';
import { formatDate } from '@/lib/format';

const ROLES_FOR_NORMAL = ['ADMIN', 'SUPERVISOR', 'ATTENDANT', 'CLIENT'];

// Mapeamento de roles para IDs
const ROLE_TO_ID = {
  'SUPER_ADMIN': 1,
  'ADMIN': 2,
  'SUPERVISOR': 3,
  'ATTENDANT': 4,
  'CLIENT': 5
};

const ID_TO_ROLE = {
  1: 'SUPER_ADMIN',
  2: 'ADMIN',
  3: 'SUPERVISOR',
  4: 'ATTENDANT',
  5: 'CLIENT'
};

export default function UsersPage() {
  const { user, hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, pageSize: 10, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusF, setStatusF] = useState('all');
  const [companyF, setCompanyF] = useState('all');
  const [companies, setCompanies] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', companyId: '', roles: ['ATTENDANT'], status: 'ACTIVE' });
  const [saving, setSaving] = useState(false);
  const [pwdModal, setPwdModal] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const isSA = hasRole('SUPER_ADMIN');
  const availableRoles = isSA ? ['SUPER_ADMIN', ...ROLES_FOR_NORMAL] : ROLES_FOR_NORMAL;

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, pageSize: 10 };
      if (q) params.q = q;
      if (statusF !== 'all') params.status = statusF;
      if (isSA && companyF !== 'all') params.companyId = companyF;
      const { data } = await api.get('/users', { params });
      setItems(data.items);
      setMeta({ page: data.page, pageSize: data.pageSize, total: data.total, totalPages: data.totalPages });
    } catch (err) { console.error('Failed to load users:', err); toast.error('Falha ao carregar usuários'); }
    finally { setLoading(false); }
  }, [q, statusF, companyF, isSA]);

  useEffect(() => { load(1); }, [load]);
  useEffect(() => {
    if (!isSA) return;
    api.get('/companies', { params: { pageSize: 100 } })
      .then(({ data }) => setCompanies(data.items))
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load companies for filter:', err);
      });
  }, [isSA]);

  const openCreate = () => { setEditing(null); setForm({ name: '', email: '', password: '', companyId: isSA ? '' : user.companyId, roles: ['ATTENDANT'], status: 'ACTIVE' }); setOpenModal(true); };
  const openEdit = (u) => { 
    setEditing(u); 
    // Converter roles do backend (objetos com type) para array de strings
    const userRoles = (u.roles || []).map(r => r.type || r);
    setForm({ 
      name: u.name, 
      email: u.email, 
      password: '', 
      companyId: u.companyId || '', 
      roles: userRoles, 
      status: u.status || 'ACTIVE' 
    }); 
    setOpenModal(true); 
  };

  const save = async () => {
    setSaving(true);
    try {
      // Converter roles (strings) para roleIds (números)
      const roleIds = form.roles.map(r => ROLE_TO_ID[r]).filter(Boolean);
      
      if (editing) {
        const { password, roles, ...rest } = form;
        await api.patch(`/users/${editing.id}`, { ...rest, roleIds });
        toast.success('Usuário atualizado');
      } else {
        const { roles, ...rest } = form;
        await api.post('/users', { ...rest, roleIds });
        toast.success('Usuário criado');
      }
      setOpenModal(false); load(meta.page);
    } catch (e) { 
      console.error('Erro ao salvar usuário:', e.response?.data);
      toast.error(e.response?.data?.message || e.response?.data?.detail || 'Erro ao salvar'); 
    }
    finally { setSaving(false); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}/toggle-active`); toast.success(u.isActive ? 'Usuário desativado' : 'Usuário ativado'); load(meta.page); }
    catch { toast.error('Erro'); }
  };

  const remove = async () => {
    try { await api.delete(`/users/${confirmDel.id}`); toast.success('Usuário excluído'); setConfirmDel(null); load(meta.page); }
    catch { toast.error('Erro ao excluir'); }
  };

  const toggleRole = (r) => {
    if (form.roles.includes(r)) setForm({ ...form, roles: form.roles.filter(x => x !== r) });
    else setForm({ ...form, roles: [...form.roles, r] });
  };

  const PwdModal = () => {
    const [npw, setNpw] = useState('');
    const [cpw, setCpw] = useState('');
    const submit = async () => {
      if (npw.length < 6) return toast.error('Senha mínima de 6 caracteres');
      try { await api.patch(`/users/${pwdModal.id}/change-password`, { newPassword: npw, currentPassword: cpw || undefined }); toast.success('Senha alterada'); setPwdModal(null); }
      catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    };
    return (
      <Dialog open={!!pwdModal} onOpenChange={(o) => !o && setPwdModal(null)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Alterar senha de {pwdModal?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {pwdModal?.id === user?.id && !isSA && <div><Label>Senha atual</Label><Input type="password" value={cpw} onChange={(e) => setCpw(e.target.value)} /></div>}
            <div><Label>Nova senha</Label><Input type="password" value={npw} onChange={(e) => setNpw(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdModal(null)}>Cancelar</Button>
            <Button onClick={submit} className="bg-blue-600 hover:bg-blue-700">Alterar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Users className="w-6 h-6 text-blue-600" /> Usuários</h1>
          <p className="text-sm text-slate-500">Gerencie quem tem acesso ao sistema.</p>
        </div>
        <Button data-testid="new-user-btn" onClick={openCreate} className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-2" /> Novo Usuário</Button>
      </div>

      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Buscar por nome, e-mail..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
          <Select value={statusF} onValueChange={setStatusF}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              {Object.entries(USER_STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          {isSA && (
            <Select value={companyF} onValueChange={setCompanyF}>
              <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as empresas</SelectItem>
                {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        {loading ? <LoadingState /> : items.length === 0 ? <EmptyState title="Nenhum usuário" icon={Users} /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Usuário</th>
                  {isSA && <th className="px-4 py-3 font-medium">Empresa</th>}
                  <th className="px-4 py-3 font-medium">Perfis</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Último login</th>
                  <th className="px-4 py-3 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{u.name}</div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                    </td>
                    {isSA && <td className="px-4 py-3 text-slate-700">{u.company?.name || '-'}</td>}
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(u.roles || []).map(r => <span key={r.id || r.type || r} className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">{ROLE_LABEL[r.type || r] || r.type || r}</span>)}
                      </div>
                    </td>
                    <td className="px-4 py-3"><ActiveBadge active={u.isActive} /></td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{u.lastLoginAt ? formatDate(u.lastLoginAt, true) : 'Nunca'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(u)} title="Editar"><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setPwdModal(u)} title="Alterar senha"><Key className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => toggleActive(u)} title={u.isActive ? 'Desativar' : 'Ativar'}><Power className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setConfirmDel(u)} title="Excluir" className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination {...meta} onPageChange={(p) => load(p)} />
      </Card>

      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle>{editing ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>E-mail *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} /></div>
            {!editing && <div><Label>Senha *</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>}
            {isSA && (
              <div><Label>Empresa *</Label>
                <Select value={form.companyId} onValueChange={(v) => setForm({ ...form, companyId: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Perfis *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {availableRoles.map(r => (
                  <label key={r} className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                    <Checkbox checked={form.roles.includes(r)} onCheckedChange={() => toggleRole(r)} />
                    <span className="text-sm">{ROLE_LABEL[r]}</span>
                  </label>
                ))}
              </div>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(USER_STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando</> : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Excluir usuário?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{confirmDel?.name} será desativado e não poderá fazer login.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancelar</Button>
            <Button onClick={remove} className="bg-red-600 hover:bg-red-700">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PwdModal />
    </div>
  );
}
